import React, { useState } from 'react';
import { FileText, BookOpen, GraduationCap } from 'lucide-react';

import KnowledgeGraph from './components/KnowledgeGraph';
import HKDSEGrammar from './components/HKDSEGrammar';
import PrimaryEnglish from './components/PrimaryEnglish';
import FloatingChatWindow from './components/FloatingChatWindow';
import ChatWithAIButton from './components/ChatWithAIButton';
import PopupSetting from './components/PopupSetting';
import A2AServerToggle from './components/A2AServerToggle';
import { UISettingsProvider, useUISettings } from './components/UISettingsContext';

const App = () => {
  const [activeTab, setActiveTab] = useState('essay');
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [chatContext, setChatContext] = useState({});

  // Chat functions
  const handleOpenChat = (context) => {
    setChatContext(context);
    setIsChatOpen(true);
  };

  const handleCloseChat = () => {
    setIsChatOpen(false);
    setChatContext({});
  };



  const handleReturnToTopic = () => {
    console.log('Returning to topic:', chatContext);
  };

  const { tabLabelMode } = useUISettings();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with Unsplash Background */}
      <div 
        className="relative bg-blue-600 text-white p-8"
        style={{
          backgroundImage: 'linear-gradient(rgba(37, 99, 235, 0.8), rgba(37, 99, 235, 0.8)), url(https://images.unsplash.com/photo-1455390582262-044cdead277a?ixlib=rb-4.0.3&auto=format&fit=crop&w=1973&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="relative z-10 max-w-7xl mx-auto">
          <h1 className="text-4xl font-bold mb-3">PriEnglishDaily</h1>
          <p className="text-blue-100 text-lg">Create professional letters and emails with AI-powered templates</p>
        </div>
      </div>

      {/* Navigation */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex space-x-8">
            {[
              { id: 'essay', label: 'Knowledge Graph', icon: FileText },
              { id: 'primary', label: 'Primary English', icon: GraduationCap },
              { id: 'grammar', label: 'Grammar', icon: BookOpen }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-2 border-b-2 font-medium text-sm transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tabLabelMode === 'icon-text' && <span>{tab.label}</span>}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Knowledge Graph Tab */}
        {activeTab === 'essay' && (
          <KnowledgeGraph />
        )}

        {/* Primary English Tab */}
        {activeTab === 'primary' && (
          <PrimaryEnglish />
        )}

        {/* HKDSE Grammar Tab */}
        {activeTab === 'grammar' && (
          <HKDSEGrammar />
        )}
      </div>

      {/* Global Floating Chat Button - only show when not in grammar tab */}
      {activeTab !== 'grammar' && (
        <ChatWithAIButton
          onClick={handleOpenChat}
          context={{ title: 'PriEnglishDaily Assistant', id: 'essay-general' }}
          position="floating"
          variant="floating"
          size="large"
        />
      )}

      {/* Floating Chat Window */}
      <FloatingChatWindow
        isOpen={isChatOpen}
        onClose={handleCloseChat}
        context={chatContext}

        onReturnToTopic={handleReturnToTopic}
      />

      {/* Popup Settings */}
      <PopupSetting />

      {/* A2A Server Toggle */}
      <A2AServerToggle />
    </div>
  );
};

const AppWithProviders = () => (
  <UISettingsProvider>
    <App />
  </UISettingsProvider>
);

export default AppWithProviders;
